import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchSummaryThunk, fetchAchievementsThunk } from 'actions/gamificationActions';
import {
  getGamificationSummary,
  getAchievements,
  getHasFetchedAchievements,
} from 'store/selectors/gamification.selector';
import { AppDispatch } from 'store/store';
import { getIsGamificationEnabled } from 'store/selectors/auth.selector';
import { getCurrentLenderDbId } from 'store/selectors/lenders.selector';
import { useIsGamificationVisibleHere } from 'modules/core/hooks/useIsGamificationVisibleHere';
import { useIsRacContext } from 'modules/core/hooks/useIsRacContext';
import { Panel } from 'modules/core/components/Panel/Panel';
import { IAchievementWithProgress } from 'modules/core/types/gamification';
import { STATIC_IMAGE_URLS } from 'modules/core/constants/staticImagesUrls.const';

import {
  ProgressCardWrapper,
  ProgressCardTitle,
  ProgressCardGaugeWrapper,
  ProgressCardGaugeText,
  ProgressCardPoints,
  ProgressCardPointsLabel,
  ProgressCardSubtitle,
  ProgressCardReward,
  ProgressCardContainer,
  RewardButton,
  RewardButtonIcon,
  ProgressCardsColumn,
  ProgressCardRowDivider,
  WavesDecoration,
} from './styles';
import { CircularProgress } from './CircularProgress';
import { AchievementListRow, VARIANTS } from './AchievementListRow';

type AchievementProgressCardVariant = 'search' | 'modal';

const PANEL_BODY_STYLE = { height: '100%', padding: 0 };

interface IAchievementProgressCardProps {
  fallbackContent?: React.ReactNode;
  noBorder?: boolean;
  variant?: AchievementProgressCardVariant;
}

type ActiveAchievement = IAchievementWithProgress & {
  completedToday: boolean;
  computedPercentage: number;
};

export const isCompletedToday = (completedAt: string | null): boolean => {
  if (!completedAt) return false;

  const today = new Date();
  const d = new Date(completedAt);

  return (
    d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate()
  );
};

export const AchievementProgressCard: React.FC<IAchievementProgressCardProps> = ({
  fallbackContent,
  noBorder = false,
  variant = 'search',
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const gamificationEnabled = useSelector(getIsGamificationEnabled);
  const gamificationVisibleHere = useIsGamificationVisibleHere();
  const summary = useSelector(getGamificationSummary);
  const achievements = useSelector(getAchievements);
  const hasFetchedAchievements = useSelector(getHasFetchedAchievements);
  const currentLenderDbId = useSelector(getCurrentLenderDbId);
  const isRacContext = useIsRacContext();

  useEffect(() => {
    if (!gamificationEnabled) return;

    if (!summary) dispatch(fetchSummaryThunk());
    dispatch(fetchAchievementsThunk());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch only on enable
  }, [dispatch, gamificationEnabled]);

  const matchesContext = useCallback(
    (a: { lender_id: number | null; is_rac: boolean }): boolean => {
      if (a.is_rac) return isRacContext;

      if (a.lender_id === null) return true;

      return !isRacContext && a.lender_id === currentLenderDbId;
    },
    [isRacContext, currentLenderDbId],
  );

  const lenderFilteredAchievements = useMemo(() => achievements.filter(matchesContext), [achievements, matchesContext]);

  const activeAchievements = useMemo((): ActiveAchievement[] => {
    if (lenderFilteredAchievements.length === 0) return [];

    const byKey = new Map<string, ActiveAchievement>();
    const achievementKey = (a: IAchievementWithProgress) =>
      `${a.activity_type}:${a.is_rac ? 'rac' : a.lender_id ?? 'global'}`;

    lenderFilteredAchievements
      .filter((a) => a.is_completed && isCompletedToday(a.completed_at))
      .forEach((a) => {
        const key = achievementKey(a);

        if (!byKey.has(key)) {
          byKey.set(key, {
            ...a,
            completedToday: true,
            computedPercentage: 100,
          });
        }
      });

    lenderFilteredAchievements
      .filter((a) => !a.is_completed && a.current_count > 0)
      .forEach((a) => {
        const key = achievementKey(a);

        if (!byKey.has(key)) {
          byKey.set(key, {
            ...a,
            completedToday: false,
            computedPercentage: Math.round((a.current_count / a.threshold) * 100),
          });
        }
      });

    return Array.from(byKey.values()).slice(0, 3);
  }, [lenderFilteredAchievements]);

  const displayList = useMemo((): ActiveAchievement[] => {
    if (activeAchievements.length > 0) return activeAchievements;

    const ca = summary?.closest_achievement;

    if (!ca || ca.current_count === 0) return [];

    if (!matchesContext(ca)) return [];

    return [
      {
        id: ca.id,
        name: ca.name,
        description: ca.description,
        activity_type: ca.activity_type,
        threshold: ca.threshold,
        order: 0,
        current_count: ca.current_count,
        is_completed: ca.completed_today,
        completed_at: ca.completed_today ? new Date().toISOString() : null,
        reward: ca.reward,
        lender_id: ca.lender_id,
        is_rac: ca.is_rac,
        completedToday: ca.completed_today,
        computedPercentage: ca.percentage,
      },
    ];
  }, [activeAchievements, summary, matchesContext]);

  if (!gamificationVisibleHere) {
    return fallbackContent ? <>{fallbackContent}</> : null;
  }

  if (displayList.length === 0) {
    if (!hasFetchedAchievements) return null;

    return fallbackContent ? <>{fallbackContent}</> : null;
  }

  const count = displayList.length;
  const isMulti = count > 1;

  const panelStyle = {
    height: '100%',
    width: '100%',
    border: noBorder ? 'none' : undefined,
    marginLeft: noBorder ? 0 : undefined,
  };

  if (!isMulti) {
    const a = displayList[0];
    const actionLabel =
      a.activity_type === 'submission' ? t('gamification.badge.submissions') : t('gamification.badge.searches');
    const gauge = VARIANTS[variant].single[a.completedToday ? 'completed' : 'active'];

    return (
      <Panel style={panelStyle} bodyStyle={PANEL_BODY_STYLE}>
        <ProgressCardWrapper>
          <ProgressCardContainer>
            <ProgressCardTitle>{a.name}</ProgressCardTitle>

            <ProgressCardGaugeWrapper $compact={a.completedToday}>
              <CircularProgress percentage={a.computedPercentage} size={gauge.circle} strokeWidth={gauge.stroke} />
              <ProgressCardGaugeText>
                {a.completedToday ? (
                  <img src={STATIC_IMAGE_URLS.icons.checkBlueCompletion} alt="" width={gauge.icon} />
                ) : (
                  <>
                    <ProgressCardPoints>
                      {a.current_count}/{a.threshold}
                    </ProgressCardPoints>
                    <ProgressCardPointsLabel>{actionLabel}</ProgressCardPointsLabel>
                  </>
                )}
              </ProgressCardGaugeText>
            </ProgressCardGaugeWrapper>

            {a.completedToday && (
              <ProgressCardSubtitle>
                {a.current_count}/{a.threshold} {actionLabel}
              </ProgressCardSubtitle>
            )}
          </ProgressCardContainer>

          {a.reward && (
            <ProgressCardReward>
              {a.completedToday ? (
                <RewardButton
                  href={a.reward.link_url || undefined}
                  target={a.reward.link_url ? '_blank' : undefined}
                  rel={a.reward.link_url ? 'noopener noreferrer' : undefined}
                  $disabled={!a.reward.link_url}
                  $margin={noBorder ? '10px 0 0 0' : undefined}
                >
                  <RewardButtonIcon
                    src={STATIC_IMAGE_URLS.icons.unlockWhiteBackground}
                    alt=""
                    $disabled={!a.reward.link_url}
                  />
                  {a.reward.link_label || t('gamification.progressCard.claimReward')}
                </RewardButton>
              ) : (
                <RewardButton as="span" $disabled>
                  <RewardButtonIcon src={STATIC_IMAGE_URLS.icons.unlockWhiteBackground} alt="" $disabled />
                  {a.reward.link_label || t('gamification.progressCard.claimReward')}
                </RewardButton>
              )}
            </ProgressCardReward>
          )}
          <WavesDecoration src={STATIC_IMAGE_URLS.backgroundWaves} alt="" aria-hidden="true" />
        </ProgressCardWrapper>
      </Panel>
    );
  }

  const density = count === 2 ? 'double' : 'triple';

  return (
    <Panel style={panelStyle} bodyStyle={PANEL_BODY_STYLE}>
      <ProgressCardsColumn>
        {displayList.map((a, index) => (
          <React.Fragment key={a.id}>
            {index > 0 && <ProgressCardRowDivider />}
            <AchievementListRow
              achievement={a}
              percentage={a.computedPercentage}
              context={variant}
              density={density}
              isCompleted={a.completedToday}
            />
          </React.Fragment>
        ))}
      </ProgressCardsColumn>
    </Panel>
  );
};
